import { useState, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { AiImportRequest } from '@/api/types/appraisal.types';

interface ImportAiDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (data: AiImportRequest) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function ImportAiDialog({ open, onClose, onImport, isLoading, error }: ImportAiDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setIsDragOver(false);
    setParseError(null);
    setConfirmOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = () => {
    if (isLoading) return;
    resetState();
    onClose();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.json')) {
      setSelectedFile(file);
      setParseError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setParseError(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChooseFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setParseError(null);
  };

  const handleImportClick = () => {
    if (!selectedFile) return;
    setConfirmOpen(true);
  };

  const handleConfirmCancel = () => {
    if (isLoading) return;
    setConfirmOpen(false);
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    try {
      const text = await selectedFile.text();
      const parsed = JSON.parse(text) as AiImportRequest;

      if (!parsed.version) {
        setConfirmOpen(false);
        setParseError("We couldn't process the uploaded file. No data has been imported.");
        return;
      }

      await onImport(parsed);
      setConfirmOpen(false);
    } catch {
      setConfirmOpen(false);
      setParseError("We couldn't process the uploaded file. No data has been imported.");
    }
  };

  const displayError = error || parseError;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import from AI</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Importing will overwrite existing content in the editable sections of this appraisal (Performance Summary, Notes to Talent Committee, Skill Area Feedback, and Forward Looking Plan).
        </Alert>

        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayError}
          </Alert>
        )}

        {!selectedFile ? (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'grey.400',
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragOver ? 'action.hover' : 'transparent',
              transition: 'all 0.2s ease',
            }}
            onClick={handleChooseFileClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleChooseFileClick(); } }}
            aria-label="Upload file drop zone"
          >
            <UploadFileIcon sx={{ fontSize: 40, color: 'grey.500', mb: 1 }} />
            <Typography variant="body1">
              Drag & Drop or{' '}
              <Typography component="span" sx={{ color: 'secondary.main', cursor: 'pointer', fontWeight: 500 }}>
                Choose file
              </Typography>
              {' '}to upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              .json
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1">{selectedFile.name}</Typography>
            </Box>
            <IconButton onClick={handleRemoveFile} disabled={isLoading} size="small" aria-label="Remove file">
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".json"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleImportClick}
          variant="contained"
          color="secondary"
          disabled={!selectedFile || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isLoading ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>

      <Dialog
        open={confirmOpen}
        onClose={handleConfirmCancel}
        maxWidth="xs"
        fullWidth
        aria-labelledby="import-confirm-title"
      >
        <DialogTitle id="import-confirm-title">Confirm import</DialogTitle>
        <DialogContent>
          <Typography>
            Uploading the file will overwrite the current appraisal form content. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} color="secondary" disabled={isLoading}>
            No
          </Button>
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            color="secondary"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isLoading ? 'Importing...' : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
